import clsx from 'clsx';
import { AlertTriangle, Archive, ArchiveRestore, CalendarRange, CloudDownload, ExternalLink, FolderInput, Link2, Palette, Pencil, Plus, RefreshCw, Search, Trash2, Unlink } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { APPROVAL_MODES, BILLING_TYPES, HOAI_TGA_PHASES, HOLIDAY_CALENDARS, isClashName, PROJECT_BILLING_MODES, PROJECT_COLORS, splitFormaName, type ApprovalMode, type BillingType, type ProjectColor } from '@pz/core';
import { PlanBar } from '../components/charts';
import { FormaIssueLink, formaLink, LocationSelects, ProjectDot, SourceSwitch, TaskBadges, type SourceFilter } from '../components/entry';
import { ProjectKpiPanel } from '../components/project-kpi';
import { EMPTY_FIELD_FILTER, fieldFilterActive, fieldFilterInput, TaskFieldFilters, type TaskFieldFilter } from '../components/task-filters';
import {
  Button,
  Checkbox,
  ConfirmModal,
  EmptyState,
  ErrorBox,
  Field,
  Gauge,
  IconButton,
  Input,
  Loading,
  matchesSearch,
  Modal,
  Notice,
  PageHeader,
  Panel,
  Pill,
  Select,
  SortTh,
  Tabs,
  Textarea,
  td,
  th,
  useToast,
} from '../components/ui';
import { FieldScopeButton, FieldScopeDialog, useScopeSummary } from '../components/field-scope';
import { ProjectStaffing } from '../components/project-staffing';
import { useTableSort } from '../lib/sort';
import { useCategories } from '../lib/categories';
import { d, dt, h, hh, money, pct } from '../lib/format';
import { useM, useQ, useTabParam } from '../lib/hooks';
import { ScrollTable, Sentinel, useChunked } from '../components/virtual';
import { useMe } from '../lib/me';
import { useSticky } from '../lib/sticky';
import { rowCheck, useRangeSelection } from '../lib/selection';
import { ApplyTemplateDialog } from '../components/task-templates';
import { ManualTaskDialog, type EditableManualTask } from './Tasks';
import { ProjectAnalysis } from './Analysis';
import { MoneyInput } from './Billing';

const FORMA_TONE: Record<string, 'neutral' | 'good' | 'warn' | 'bad'> = { not_linked: 'neutral', active: 'good', deleted: 'bad', access_lost: 'bad' };

export function ProjectsPage() {
  const { t } = useTranslation();
  const { can } = useMe();
  const toast = useToast();
  const [search, setSearch] = useState('');
  // current projects, only the archive, or both (archived projects and their tasks are hidden elsewhere)
  const [archived, setArchived] = useSticky<'active' | 'only' | 'all'>('projects.archivedMode', 'active');
  const [create, setCreate] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // only the own projects (member or project lead) or all visible ones (request 21); the choice follows the person to
  // every device. Not chosen yet: the office (projects.manage) starts with all projects, everybody else with the own ones
  const listPref = useQ('prefs.get', { keys: ['projects.list'] });
  const setPref = useM('prefs.set', { invalidate: ['prefs.'] });
  const scope: 'mine' | 'all' = listPref.data?.['projects.list']?.scope ?? (can('projects.manage') ? 'all' : 'mine');
  const q = useQ('projects.list', { archived, onlyMine: scope === 'mine' }, { enabled: !listPref.isLoading });
  const archiveMany = useM('projects.archiveMany', { invalidate: ['projects.', 'tasks.', 'me.'] });
  const assignColors = useM('projects.assignColors', { invalidate: ['projects.', 'tasks.', 'time.', 'quick.'] });
  type ProjectRow = NonNullable<typeof q.data>['projects'][number];
  const sorting = useTableSort<ProjectRow>('projects', {
    number: (p) => p.number,
    name: (p) => p.name,
    billing: (p) => p.billingMode,
    role: (p) => (p.myRole === 'lead' ? 0 : p.myRole ? 1 : null),
    members: (p) => p.memberCount,
    booked: (p) => p.bookedMinutes,
  });
  const list = useMemo(
    () => sorting.apply((q.data?.projects ?? []).filter((p) => matchesSearch(`${p.number} ${p.name} ${p.formaName ?? ''} ${p.customerName ?? ''}`, search))),
    [q.data, search, sorting.sort], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const chunk = useChunked(list, 100, `${search}|${archived}|${sorting.sort?.key}|${sorting.sort?.dir}`);
  const manage = can('projects.manage');
  const order = useMemo(() => list.map((p) => p.id), [list]);
  const sel = useRangeSelection(order);
  const chosen = list.filter((p) => sel.selected.has(p.id));
  const toArchive = chosen.filter((p) => !p.archived).map((p) => p.id);
  const toRestore = chosen.filter((p) => p.archived).map((p) => p.id);

  const bulk = (ids: string[], value: boolean) =>
    archiveMany
      .mutateAsync({ ids, archived: value })
      .then((r) => {
        sel.clear();
        toast.undoable(t(value ? 'projects.archivedMany' : 'projects.restoredMany', { count: r.changed }), t('common.undo'), () =>
          archiveMany.mutateAsync({ ids, archived: !value }).then(() => toast.ok(t('common.undone'))).catch(toast.error),
        );
      })
      .catch(toast.error);

  return (
    <div className={clsx(sel.count > 0 && 'pb-16')}>
      <PageHeader
        title={t('projects.title')}
        actions={
          manage && (
            <>
              {(q.data?.projects ?? []).some((p) => !p.archived && !p.color && p.billingMode !== 'internal') && (
                <Button
                  icon={<Palette className="size-4" />}
                  title={t('projects.assignColorsHint')}
                  loading={assignColors.isPending}
                  onClick={() =>
                    assignColors
                      .mutateAsync({})
                      .then((r) => toast.ok(t('projects.assignedColors', { count: r.assigned })))
                      .catch(toast.error)
                  }
                >
                  {t('projects.assignColors')}
                </Button>
              )}
              <Button icon={<CloudDownload className="size-4" />} onClick={() => setImportOpen(true)}>
                {t('projects.importForma')}
              </Button>
              <Button icon={<Plus className="size-4" />} onClick={() => setCreate(true)}>
                {t('projects.new')}
              </Button>
            </>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-line bg-sheet p-3">
        <div className="relative min-w-64 flex-1">
          <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-faint" />
          <Input className="pl-8" placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex rounded-md border border-line p-0.5" role="group" aria-label={t('projects.listScope.label')}>
          {(['mine', 'all'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={scope === s}
              onClick={() => setPref.mutateAsync({ key: 'projects.list', value: { scope: s } }).catch(toast.error)}
              className={clsx('rounded px-3 py-1 text-sm', scope === s ? 'bg-accent text-white' : 'text-graphite hover:bg-sheet-2 hover:text-ink')}
            >
              {t(`projects.listScope.${s}`)}
            </button>
          ))}
        </div>
        <Select className="w-48" value={archived} onChange={(e) => setArchived(e.target.value as 'active')} aria-label={t('projects.archiveFilter')}>
          <option value="active">{t('projects.archiveModes.active')}</option>
          <option value="only">{t('projects.archiveModes.only')}</option>
          <option value="all">{t('projects.archiveModes.all')}</option>
        </Select>
        {scope === 'mine' && <p className="w-full text-xs text-graphite">{t('projects.listScope.mineHint')}</p>}
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && list.length === 0 && <EmptyState>{t('common.noResults')}</EmptyState>}
      {list.length > 0 && (
        <>
        <ScrollTable label={t('projects.title')}>
          <table className="w-full min-w-[880px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                {manage && (
                  <th className={clsx(th, 'w-8')}>
                    <Checkbox label={<span className="sr-only">{t('common.selectAll')}</span>} checked={sel.all} onChange={sel.toggleAll} />
                  </th>
                )}
                <SortTh label={t('projects.number')} sortKey="number" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('projects.name')} sortKey="name" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('projects.billingMode.label')} sortKey="billing" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('projects.myRole')} sortKey="role" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('projects.members')} sortKey="members" sort={sorting.sort} onSort={sorting.toggle} right />
                <th className={th}>Forma</th>
                <SortTh label={t('common.budget')} sortKey="booked" sort={sorting.sort} onSort={sorting.toggle} className="w-56" />
              </tr>
            </thead>
            <tbody>
              {chunk.visible.map((p) => (
                <tr key={p.id} className={clsx('border-b border-line/70 hover:bg-sheet-2', p.archived && 'text-graphite', sel.selected.has(p.id) && 'bg-accent-soft/50')}>
                  {manage && (
                    <td className={td}>
                      <Checkbox
                        label={<span className="sr-only">{t('common.select')}</span>}
                        {...rowCheck(sel, p.id)}
                      />
                    </td>
                  )}
                  <td className={clsx(td, 'num font-semibold')}>
                    <span className="inline-flex items-center gap-1.5">
                      <ProjectDot color={p.color} />
                      <Link to={`/projects/${p.id}`} className="text-accent hover:underline">
                        {p.number}
                      </Link>
                    </span>
                  </td>
                  <td className={td}>
                    <Link to={`/projects/${p.id}`} className="hover:text-accent">
                      {p.name}
                    </Link>
                    <div className="text-xs text-graphite">
                      {[p.customerName, p.formaName && p.formaName !== p.name ? `Forma: ${p.formaName}` : null].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td className={td}>{t(`projects.billingMode.${p.billingMode as 'hourly'}`)}</td>
                  <td className={td}>{p.myRole && <Pill tone={p.myRole === 'lead' ? 'accent' : 'neutral'}>{t(`projects.role.${p.myRole as 'lead'}`)}</Pill>}</td>
                  <td className={clsx(td, 'num text-right')}>{p.memberCount}</td>
                  <td className={td}>
                    <span className="flex flex-wrap gap-1">
                      <Pill tone={FORMA_TONE[p.formaState]}>{t(`projects.formaState.${p.formaState as 'active'}`)}</Pill>
                      {p.formaWarnings > 0 && (
                        <Pill tone="warn">
                          <AlertTriangle className="size-3" />
                          {p.formaWarnings}
                        </Pill>
                      )}
                      {p.archived && <Pill>{t('common.archived')}</Pill>}
                      {!p.archived && !p.bookable && (
                        <Pill tone="warn" title={t('projects.booking.closedHint')}>
                          {t('projects.booking.closedShort')}
                        </Pill>
                      )}
                      {!p.archived && p.status !== 'active' && <Pill title={t('projects.syncPausedHint')}>{t(`projects.statuses.${p.status as 'on_hold'}`)}</Pill>}
                    </span>
                  </td>
                  <td className={td}>
                    {p.bookedMinutes !== null && (
                      <div className="flex flex-col gap-1">
                        <span className="num text-xs text-graphite">
                          {h(p.bookedMinutes, 0)}
                          {p.budgetMinutes ? ` / ${hh(p.budgetMinutes, 0)}` : ` ${t('common.h')}`}
                        </span>
                        {!!p.budgetMinutes && <Gauge value={p.bookedMinutes} capacity={p.budgetMinutes} />}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {chunk.hasMore && <Sentinel onMore={chunk.more} />}
        </ScrollTable>
        <p className="mt-2 text-xs text-graphite">{chunk.hasMore ? t('entries.showing', { shown: chunk.shown, total: chunk.total }) : t('projects.count', { count: chunk.total })}</p>
        </>
      )}
      {manage && sel.count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-sheet px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] lg:left-60">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2">
            <span className="text-sm">{t('common.selected', { count: sel.count })}</span>
            <span className="text-xs text-graphite">{t('common.shiftHint')}</span>
            <div className="ml-auto flex gap-2">
              <Button onClick={sel.clear}>{t('common.cancel')}</Button>
              {toRestore.length > 0 && (
                <Button icon={<ArchiveRestore className="size-4" />} loading={archiveMany.isPending} onClick={() => bulk(toRestore, false)}>
                  {t('projects.restoreCount', { count: toRestore.length })}
                </Button>
              )}
              {toArchive.length > 0 && (
                <Button variant="primary" icon={<Archive className="size-4" />} loading={archiveMany.isPending} onClick={() => bulk(toArchive, true)}>
                  {t('projects.archiveCount', { count: toArchive.length })}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {archived !== 'only' && <ProjectStaffing />}
      <ProjectFormModal open={create} onClose={() => setCreate(false)} />
      {importOpen && <FormaImportModal onClose={() => setImportOpen(false)} />}
    </div>
  );
}

/**
 * Takes over Forma projects in one step: creates the Projektzeit project (number and name from the
 * Forma name), links it and switches the synchronisation on. For offices with many Forma projects.
 */
function FormaImportModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useQ('forma.projects', {});
  const run = useM('projects.importFromForma', { invalidate: ['projects.', 'forma.', 'me.'] });
  const [search, setSearch] = useState('');
  const [billingMode, setBillingMode] = useState('hourly');
  const [billingType, setBillingType] = useState<BillingType>('billable');
  const [openBooking, setOpenBooking] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);
  const rows = useMemo(() => (q.data?.projects ?? []).filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase())), [q.data, search]);
  const free = useMemo(() => rows.filter((p) => !p.linkedTo).map((p) => p.id), [rows]);
  const sel = useRangeSelection(free);
  const start = () =>
    run
      .mutateAsync({
        items: (q.data?.projects ?? []).filter((p) => sel.selected.has(p.id)).map((p) => ({ formaProjectId: p.id, name: p.name })),
        billingMode: billingMode as 'hourly',
        defaultBillingType: billingType,
        openBooking,
      })
      .then((r) => {
        setResult({ created: r.created.length, skipped: r.skipped.length });
        sel.clear();
        toast.ok(t('projects.importDone', { count: r.created.length }));
      })
      .catch(toast.error);
  return (
    <Modal
      open
      wide
      onClose={onClose}
      title={t('projects.importForma')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.close')}</Button>
          <Button variant="primary" disabled={!sel.count} loading={run.isPending} onClick={start}>
            {t('projects.importCount', { count: sel.count })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Notice tone="neutral">{t('projects.importInfo')}</Notice>
        {result && <Notice tone="good">{t('projects.importResult', { created: result.created, skipped: result.skipped })}</Notice>}
        <ErrorBox error={q.error} />
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('projects.billingMode.label')}>
            <Select value={billingMode} onChange={(e) => setBillingMode(e.target.value)}>
              {PROJECT_BILLING_MODES.map((m) => (
                <option key={m} value={m}>
                  {t(`projects.billingMode.${m}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('projects.defaultBillingType')}>
            <Select value={billingType} onChange={(e) => setBillingType(e.target.value as BillingType)}>
              {BILLING_TYPES.map((b) => (
                <option key={b} value={b}>
                  {t(`billingType.${b}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Checkbox className="self-end pb-2" label={t('projects.openBooking')} hint={t('projects.openBookingImportHint')} checked={openBooking} onChange={(e) => setOpenBooking(e.target.checked)} />
        </div>
        <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        {q.isLoading && <Loading />}
        {q.data && (
          <div className="max-h-[50vh] overflow-y-auto rounded-md border border-line">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b border-line bg-sheet-2">
                <tr>
                  <th className={clsx(th, 'w-8')}>
                    <Checkbox label={<span className="sr-only">{t('common.selectAll')}</span>} checked={sel.all} onChange={sel.toggleAll} disabled={!free.length} />
                  </th>
                  <th className={th}>{t('projects.formaProject')}</th>
                  <th className={th}>{t('projects.importAs')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const split = splitFormaName(p.name);
                  return (
                    <tr key={p.id} className={clsx('border-b border-line/70', p.linkedTo && 'text-graphite')}>
                      <td className={td}>
                        {!p.linkedTo && (
                          <Checkbox
                            label={<span className="sr-only">{t('common.select')}</span>}
                            {...rowCheck(sel, p.id)}
                          />
                        )}
                      </td>
                      <td className={td}>{p.name}</td>
                      <td className={td}>
                        {p.linkedTo ? (
                          <Pill tone="good">{t('projects.alreadyLinked', { number: p.linkedTo.number })}</Pill>
                        ) : (
                          <span>
                            <span className="num font-semibold">{split.number ?? t('projects.autoNumber')}</span> {split.name}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-graphite">{t('common.shiftHint')}</p>
      </div>
    </Modal>
  );
}

interface ProjectForm {
  number: string;
  name: string;
  customerName: string;
  customerAddress: string;
  partner: string;
  siteLocation: string;
  holidayCalendar: string;
  billingMode: string;
  defaultBillingType: BillingType;
  openBooking: boolean;
  budgetHours: string;
  feeTotalCents: number | null;
  startDate: string;
  endDate: string;
  status: string;
  notes: string;
  /** colour of the project ('' = none; a new project gets the least used colour) */
  color: string;
}

const EMPTY_FORM: ProjectForm = {
  number: '',
  name: '',
  customerName: '',
  customerAddress: '',
  partner: '',
  siteLocation: '',
  holidayCalendar: '',
  billingMode: 'hourly',
  defaultBillingType: 'billable',
  openBooking: false,
  budgetHours: '',
  feeTotalCents: null,
  startDate: '',
  endDate: '',
  status: 'active',
  notes: '',
  color: '',
};

function toInput(f: ProjectForm) {
  return {
    number: f.number,
    name: f.name,
    customerName: f.customerName || null,
    customerAddress: f.customerAddress || null,
    partner: f.partner || null,
    siteLocation: f.siteLocation || null,
    holidayCalendar: (f.holidayCalendar || null) as 'DE-NW' | null,
    billingMode: f.billingMode as 'hourly',
    defaultBillingType: f.defaultBillingType,
    openBooking: f.openBooking,
    budgetMinutes: f.budgetHours ? Math.round(Number(f.budgetHours.replace(',', '.')) * 60) : null,
    feeTotalCents: f.feeTotalCents,
    startDate: f.startDate || null,
    endDate: f.endDate || null,
    status: f.status as 'active',
    notes: f.notes || null,
    // a new project without a choice: the server gives it the least used colour
    color: (f.color || null) as ProjectColor | null,
  };
}

/** The eight colours of the palette and "no colour" (always shown with the project number and name). */
function ColorPicker({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  return (
    <div role="radiogroup" aria-label={t('projects.color')} className="flex flex-wrap items-center gap-1.5">
      {PROJECT_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={value === c}
          aria-label={t(`projects.colors.${c}`)}
          title={t(`projects.colors.${c}`)}
          disabled={disabled}
          onClick={() => onChange(c)}
          className={clsx('size-7 rounded-full border-2 transition-transform disabled:opacity-50', value === c ? 'scale-110 border-ink' : 'border-transparent hover:scale-105')}
          style={{ background: `var(--pc-${c})` }}
        />
      ))}
      <button
        type="button"
        role="radio"
        aria-checked={!value}
        disabled={disabled}
        onClick={() => onChange('')}
        className={clsx('h-7 rounded-full border px-2 text-xs text-graphite disabled:opacity-50', !value ? 'border-ink text-ink' : 'border-line hover:border-line-strong')}
      >
        {t('projects.colorNone')}
      </button>
    </div>
  );
}

function ProjectFields({ f, set, disabled, identityOnly }: { f: ProjectForm; set: (f: ProjectForm) => void; disabled?: boolean; identityOnly?: boolean }) {
  const { t } = useTranslation();
  const field = (k: keyof ProjectForm) => ({
    value: String(f[k] ?? ''),
    // the technical admin changes number and name only
    disabled: disabled || (identityOnly && k !== 'number' && k !== 'name'),
    onChange: (e: { target: { value: string } }) => set({ ...f, [k]: e.target.value }),
  });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label={t('projects.number')}>
        <Input {...field('number')} />
      </Field>
      <Field label={t('projects.name')}>
        <Input {...field('name')} />
      </Field>
      <Field label={t('projects.customer')}>
        <Input {...field('customerName')} />
      </Field>
      <Field label={t('projects.partner')}>
        <Input {...field('partner')} />
      </Field>
      <Field label={t('projects.customerAddress')} className="sm:col-span-2">
        <Input {...field('customerAddress')} />
      </Field>
      <Field label={t('projects.site')}>
        <Input {...field('siteLocation')} />
      </Field>
      <Field label={t('projects.calendar')}>
        <Select {...field('holidayCalendar')}>
          <option value="">-</option>
          {HOLIDAY_CALENDARS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('projects.billingMode.label')}>
        <Select {...field('billingMode')}>
          {PROJECT_BILLING_MODES.map((m) => (
            <option key={m} value={m}>
              {t(`projects.billingMode.${m}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('projects.defaultBillingType')}>
        <Select {...field('defaultBillingType')}>
          {BILLING_TYPES.map((b) => (
            <option key={b} value={b}>
              {t(`billingType.${b}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('projects.budget')}>
        <Input inputMode="decimal" {...field('budgetHours')} />
      </Field>
      <Field label={t('projects.fee')}>
        <MoneyInput value={f.feeTotalCents} onChange={(c) => set({ ...f, feeTotalCents: c })} />
      </Field>
      <Field label={t('projects.start')}>
        <Input type="date" {...field('startDate')} />
      </Field>
      <Field label={t('projects.end')}>
        <Input type="date" {...field('endDate')} />
      </Field>
      <Field label={t('projects.statusLabel')}>
        <Select {...field('status')}>
          {(['active', 'on_hold', 'completed', 'cancelled'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`projects.statuses.${s}`)}
            </option>
          ))}
        </Select>
      </Field>
      <div className="flex items-end">
        <Checkbox label={t('projects.openBooking')} hint={t('projects.openBookingHint')} checked={f.openBooking} disabled={disabled} onChange={(e) => set({ ...f, openBooking: e.target.checked })} />
      </div>
      <Field label={t('projects.color')} hint={t('projects.colorHint')} className="sm:col-span-2">
        <ColorPicker value={f.color} onChange={(c) => set({ ...f, color: c })} disabled={disabled || identityOnly} />
      </Field>
      <Field label={t('projects.notes')} className="sm:col-span-2">
        <Textarea rows={2} {...field('notes')} />
      </Field>
    </div>
  );
}

function ProjectFormModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [f, setF] = useState<ProjectForm>(EMPTY_FORM);
  useEffect(() => {
    if (open) setF(EMPTY_FORM);
  }, [open]);
  const create = useM('projects.create', { invalidate: ['projects.'] });
  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={t('projects.new')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!f.number || !f.name}
            loading={create.isPending}
            onClick={() =>
              create
                .mutateAsync(toInput(f))
                .then((r) => {
                  onClose();
                  navigate(`/projects/${r.id}`);
                })
                .catch(() => {})
            }
          >
            {t('common.create')}
          </Button>
        </>
      }
    >
      <ProjectFields f={f} set={setF} />
      <div className="mt-3">
        <ErrorBox error={create.error} />
      </div>
    </Modal>
  );
}

type Tab = 'overview' | 'analysis' | 'kpi' | 'members' | 'tasks' | 'forma' | 'fee';
const TABS: Tab[] = ['overview', 'analysis', 'kpi', 'members', 'tasks', 'forma', 'fee'];

export function ProjectPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const { me } = useMe();
  const q = useQ('projects.get', { id });
  const [tab, setTab] = useTabParam<Tab>(TABS);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const { project, permissions } = q.data;
  // the analysis of a project: right "Projektanalyse" (all projects) or the project lead
  const analysis = me.analysis === 'all' || me.leadProjectIds.includes(project.id);
  return (
    <div>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span className="num">{project.number}</span> {project.name}
            {project.archived && <Pill>{t('common.archived')}</Pill>}
            {!project.archived && project.status !== 'active' && <Pill title={t('projects.syncPausedHint')}>{t(`projects.statuses.${project.status as 'on_hold'}`)}</Pill>}
            <Pill tone={FORMA_TONE[project.forma.state]}>{t(`projects.formaState.${project.forma.state as 'active'}`)}</Pill>
          </span>
        }
        subtitle={project.customerName ?? undefined}
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: t('projects.tabs.overview') },
          { id: 'analysis', label: t('projects.tabs.analysis'), hidden: !analysis },
          { id: 'kpi', label: t('projects.tabs.kpi'), hidden: !permissions.viewEntries },
          { id: 'members', label: t('projects.tabs.members') },
          { id: 'tasks', label: t('projects.tabs.tasks') },
          { id: 'forma', label: t('projects.tabs.forma') },
          { id: 'fee', label: t('projects.tabs.fee'), hidden: !permissions.billing || project.billingMode === 'hourly' || project.billingMode === 'internal' },
        ]}
      />
      {tab === 'overview' && <OverviewTab data={q.data} onKpi={() => setTab('kpi')} />}
      {tab === 'analysis' && analysis && <ProjectAnalysis projectId={project.id} embedded />}
      {tab === 'kpi' && permissions.viewEntries && <ProjectKpiPanel projectId={project.id} />}
      {tab === 'members' && <MembersTab data={q.data} />}
      {tab === 'tasks' && <TasksTab data={q.data} />}
      {tab === 'forma' && <FormaTab data={q.data} />}
      {tab === 'fee' && <FeeTab data={q.data} />}
    </div>
  );
}

type ProjectData = NonNullable<ReturnType<typeof useProjectQuery>['data']>;

/**
 * Approval of the hours of the project (request 29 of round 4): who approves, and whether approvers may approve their
 * own hours. The office (projects.manage) sets it; everybody on the project sees which rule applies.
 */
function ApprovalPanel({ data }: { data: ProjectData }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { me } = useMe();
  const p = data.project;
  const update = useM('projects.update', { invalidate: ['projects.', 'approvals.', 'time.'] });
  const save = (patch: { approvalMode?: ApprovalMode | null; approvalSelf?: boolean | null }) =>
    update
      .mutateAsync({ id: p.id, ...patch })
      .then(() => toast.ok(t('common.saved')))
      .catch(toast.error);
  const company = t(`projects.approval.modes.${me.settings.approvalMode as ApprovalMode}`);
  return (
    <Panel title={t('projects.approval.title')}>
      <p className="mb-3 text-sm">
        <Pill tone="accent">{t(`projects.approval.modes.${p.approval.mode}`)}</Pill>
        <span className="mt-1 block text-xs text-graphite">{t(`projects.approval.hints.${p.approval.mode}`)}</span>
      </p>
      {data.permissions.edit ? (
        <div className="flex flex-col gap-3">
          <Field label={t('projects.approval.mode')}>
            <Select value={p.approvalMode ?? ''} disabled={update.isPending} onChange={(e) => save({ approvalMode: (e.target.value || null) as ApprovalMode | null })}>
              <option value="">{t('projects.approval.companyDefault', { mode: company })}</option>
              {APPROVAL_MODES.map((m) => (
                <option key={m} value={m}>
                  {t(`projects.approval.modes.${m}`)}
                </option>
              ))}
            </Select>
          </Field>
          {p.approval.mode !== 'self' && (
            <Field label={t('projects.approval.self')} hint={t('projects.approval.selfHint')}>
              <Select
                value={p.approvalSelf === null || p.approvalSelf === undefined ? '' : p.approvalSelf ? 'yes' : 'no'}
                disabled={update.isPending}
                onChange={(e) => save({ approvalSelf: e.target.value === '' ? null : e.target.value === 'yes' })}
              >
                <option value="">{t('projects.approval.selfDefault', { value: me.settings.allowSelfApproval ? t('common.yes') : t('common.no') })}</option>
                <option value="yes">{t('common.yes')}</option>
                <option value="no">{t('common.no')}</option>
              </Select>
            </Field>
          )}
          <p className="text-xs text-graphite">{t('projects.approval.changeHint')}</p>
        </div>
      ) : (
        p.approval.allowSelf && <p className="text-xs text-graphite">{t('projects.approval.selfAllowed')}</p>
      )}
    </Panel>
  );
}
function useProjectQuery(id: string) {
  return useQ('projects.get', { id });
}

function OverviewTab({ data, onKpi }: { data: ProjectData; onKpi: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const p = data.project;
  const initial: ProjectForm = useMemo(
    () => ({
      number: p.number,
      name: p.name,
      customerName: p.customerName ?? '',
      customerAddress: p.customerAddress ?? '',
      partner: p.partner ?? '',
      siteLocation: p.siteLocation ?? '',
      holidayCalendar: p.holidayCalendar ?? '',
      billingMode: p.billingMode,
      defaultBillingType: p.defaultBillingType,
      openBooking: p.openBooking,
      budgetHours: p.budgetMinutes ? String(p.budgetMinutes / 60) : '',
      feeTotalCents: p.feeTotalCents,
      startDate: p.startDate ?? '',
      endDate: p.endDate ?? '',
      status: p.status,
      notes: p.notes ?? '',
      color: p.color ?? '',
    }),
    [p],
  );
  const [f, setF] = useState(initial);
  useEffect(() => setF(initial), [initial]);
  const update = useM('projects.update', { invalidate: ['projects.'] });
  const archive = useM('projects.archive', { invalidate: ['projects.'] });
  const [confirm, setConfirm] = useState(false);
  const edit = data.permissions.edit;
  const identityOnly = !edit && data.permissions.editIdentity;
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      <Panel
        title={t('projects.tabs.overview')}
        actions={
          (edit || identityOnly) && (
            <Button
              variant="primary"
              size="sm"
              loading={update.isPending}
              onClick={() =>
                update
                  .mutateAsync(identityOnly ? { id: p.id, number: f.number, name: f.name } : { id: p.id, ...toInput(f) })
                  .then(() => toast.ok(t('common.saved')))
                  .catch(toast.error)
              }
            >
              {t('common.save')}
            </Button>
          )
        }
      >
        <ProjectFields f={f} set={setF} disabled={!edit && !identityOnly} identityOnly={identityOnly} />
        {identityOnly && <p className="mt-3 text-xs text-graphite">{t('projects.identityOnlyHint')}</p>}
      </Panel>
      <div className="flex flex-col gap-4">
        {p.bookedMinutes !== null && (
          <Panel title={t('common.budget')}>
            <div className="num text-lg font-semibold">
              {hh(p.bookedMinutes)} {p.budgetMinutes ? <span className="text-sm font-normal text-graphite">/ {hh(p.budgetMinutes)}</span> : null}
            </div>
            {!!p.budgetMinutes && <Gauge value={p.bookedMinutes} capacity={p.budgetMinutes} className="mt-2" />}
            <button type="button" onClick={onKpi} className="mt-3 inline-block text-sm text-accent hover:underline">
              {t('projects.tabs.kpi')}
            </button>
          </Panel>
        )}
        {p.billingMode !== 'internal' && (p.forma.state !== 'active' || p.bookableWithoutForma) && (
          <Panel title={t('projects.booking.title')}>
            <p className="mb-2 text-sm">
              {p.bookable ? <Pill tone="good">{t('projects.booking.open')}</Pill> : <Pill tone="warn">{t('projects.booking.closedShort')}</Pill>}
            </p>
            <p className="mb-3 text-sm text-graphite">{p.bookable ? t('projects.booking.openHint') : t('projects.booking.closedHint')}</p>
            {data.permissions.bookingRelease && (
              <Checkbox
                label={t('projects.booking.release')}
                hint={t('projects.booking.releaseHint')}
                checked={p.bookableWithoutForma}
                disabled={update.isPending || p.archived}
                onChange={(e) => update.mutateAsync({ id: p.id, bookableWithoutForma: e.target.checked }).catch(toast.error)}
              />
            )}
          </Panel>
        )}
        {(p.billingMode !== 'internal' || data.permissions.edit) && <ApprovalPanel data={data} />}
        {data.permissions.archive && (
          <Panel title={p.archived ? t('common.unarchive') : t('common.archive')}>
            <p className="mb-3 text-sm text-graphite">{t('projects.archiveText')}</p>
            <Button icon={p.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />} onClick={() => setConfirm(true)}>
              {p.archived ? t('common.unarchive') : t('common.archive')}
            </Button>
          </Panel>
        )}
      </div>
      <ConfirmModal
        open={confirm}
        onClose={() => setConfirm(false)}
        title={p.archived ? t('common.unarchive') : t('common.archive')}
        text={t('projects.archiveText')}
        confirmLabel={p.archived ? t('common.unarchive') : t('common.archive')}
        loading={archive.isPending}
        onConfirm={() =>
          archive
            .mutateAsync({ id: p.id, archived: !p.archived })
            .then(() => setConfirm(false))
            .catch(toast.error)
        }
      />
    </div>
  );
}

type MemberForm = { personId: string; role: 'lead' | 'member'; taskScope: 'all' | 'selected'; validFrom: string; validTo: string };
const EMPTY_MEMBER: MemberForm = { personId: '', role: 'member', taskScope: 'all', validFrom: '', validTo: '' };

function MembersTab({ data }: { data: ProjectData }) {
  const { t } = useTranslation();
  const toast = useToast();
  const people = useQ('people.list', {}, { enabled: data.permissions.manageMembers });
  const set = useM('projects.setMember', { invalidate: ['projects.', 'me.'] });
  const remove = useM('projects.removeMember', { invalidate: ['projects.', 'me.'] });
  // null: closed; add: new member; edit: existing member (person fixed)
  const [dialog, setDialog] = useState<{ mode: 'add' } | { mode: 'edit'; personId: string } | null>(null);
  const [form, setForm] = useState<MemberForm>(EMPTY_MEMBER);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [tasksFor, setTasksFor] = useState<string | null>(null);
  const memberIds = new Set(data.members.map((m) => m.personId));
  const editing = dialog?.mode === 'edit' ? (data.members.find((m) => m.personId === dialog.personId) ?? null) : null;
  // a lead membership is changed only by project management (same rule as on the server)
  const canEdit = (m: ProjectData['members'][number]) => data.permissions.manageMembers && (m.role !== 'lead' || data.permissions.assignLeads);
  const badRange = !!form.validFrom && !!form.validTo && form.validFrom > form.validTo;
  const becomesSelected = form.taskScope === 'selected' && editing?.taskScope !== 'selected';

  const openAdd = () => {
    set.reset();
    setForm(EMPTY_MEMBER);
    setDialog({ mode: 'add' });
  };
  const openEdit = (m: ProjectData['members'][number]) => {
    set.reset();
    setForm({
      personId: m.personId,
      role: m.role as 'lead' | 'member',
      taskScope: m.taskScope as 'all' | 'selected',
      validFrom: m.validFrom ?? '',
      validTo: m.validTo ?? '',
    });
    setDialog({ mode: 'edit', personId: m.personId });
  };
  const save = () => {
    const openTasks = becomesSelected;
    set
      .mutateAsync({ projectId: data.project.id, ...form, validFrom: form.validFrom || null, validTo: form.validTo || null })
      .then(() => {
        setDialog(null);
        // newly limited to selected tasks: choose them right away, otherwise the person cannot book here
        if (openTasks) setTasksFor(form.personId);
      })
      .catch(() => {});
  };

  return (
    <Panel
      title={t('projects.members')}
      bodyClassName="p-0"
      actions={
        data.permissions.manageMembers && (
          <Button size="sm" icon={<Plus className="size-3.5" />} onClick={openAdd}>
            {t('projects.addMember')}
          </Button>
        )
      }
    >
      <div className="scroll-x">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-line bg-sheet-2">
            <tr>
              <th className={th}>{t('common.name')}</th>
              <th className={th}>{t('common.role')}</th>
              <th className={th}>{t('projects.scope.label')}</th>
              <th className={th}>{t('projects.validFrom')}</th>
              <th className={th}>{t('projects.validTo')}</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.personId} className={clsx('border-b border-line/70', !m.active && 'text-graphite')}>
                <td className={td}>
                  <div className="font-medium">{m.name}</div>
                  <div className="text-xs text-graphite">{[m.discipline, m.locationCountry, m.kind === 'external' ? t('people.kind.external') : null].filter(Boolean).join(' · ')}</div>
                </td>
                <td className={td}>
                  {data.permissions.assignLeads ? (
                    <Select
                      className="h-8 w-40"
                      value={m.role}
                      onChange={(e) =>
                        set
                          .mutateAsync({ projectId: data.project.id, personId: m.personId, role: e.target.value as 'lead', taskScope: m.taskScope as 'all', validFrom: m.validFrom, validTo: m.validTo })
                          .catch(toast.error)
                      }
                    >
                      <option value="member">{t('projects.role.member')}</option>
                      <option value="lead">{t('projects.role.lead')}</option>
                    </Select>
                  ) : (
                    <Pill tone={m.role === 'lead' ? 'accent' : 'neutral'}>{t(`projects.role.${m.role as 'lead'}`)}</Pill>
                  )}
                </td>
                <td className={td}>
                  {t(`projects.scope.${m.taskScope as 'all'}`)}
                  {m.taskScope === 'selected' && (
                    <button className={clsx('ml-2 text-xs hover:underline', m.selectedTaskIds.length ? 'text-accent' : 'text-bad')} onClick={() => setTasksFor(m.personId)}>
                      {t('projects.selectTasks')} ({m.selectedTaskIds.length})
                    </button>
                  )}
                </td>
                <td className={clsx(td, 'num')}>{d(m.validFrom)}</td>
                <td className={clsx(td, 'num')}>{d(m.validTo)}</td>
                <td className={clsx(td, 'whitespace-nowrap text-right')}>
                  {/* also for ended memberships: extending "until" brings the person back into the project */}
                  {canEdit(m) && <IconButton label={t('projects.editMember')} icon={<Pencil className="size-3.5" />} onClick={() => openEdit(m)} />}
                  {canEdit(m) && m.active && <IconButton label={t('projects.removeMember')} icon={<Trash2 className="size-3.5" />} onClick={() => setRemoveId(m.personId)} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal
        open={!!dialog}
        onClose={() => setDialog(null)}
        title={editing ? `${t('projects.editMember')}: ${editing.name}` : t('projects.addMember')}
        footer={
          <>
            <Button onClick={() => setDialog(null)}>{t('common.cancel')}</Button>
            <Button variant="primary" disabled={!form.personId || badRange} loading={set.isPending} onClick={save}>
              {editing ? t('common.save') : t('common.add')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {!editing && (
            <Field label={t('common.person')}>
              <Select value={form.personId} onChange={(e) => setForm({ ...form, personId: e.target.value })}>
                <option value="">{t('common.select')}</option>
                {(people.data?.people ?? [])
                  .filter((p) => !memberIds.has(p.id))
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName} {p.kind === 'external' ? `(${t('people.kind.external')})` : ''}
                    </option>
                  ))}
              </Select>
            </Field>
          )}
          <div className="flex flex-wrap gap-4">
            <Field label={t('common.role')}>
              <Select value={form.role} disabled={!data.permissions.assignLeads} onChange={(e) => setForm({ ...form, role: e.target.value as 'lead' })}>
                <option value="member">{t('projects.role.member')}</option>
                <option value="lead">{t('projects.role.lead')}</option>
              </Select>
            </Field>
            <Field label={t('projects.scope.label')}>
              <Select value={form.taskScope} onChange={(e) => setForm({ ...form, taskScope: e.target.value as 'all' })}>
                <option value="all">{t('projects.scope.all')}</option>
                <option value="selected">{t('projects.scope.selected')}</option>
              </Select>
            </Field>
          </div>
          <div className="flex flex-wrap gap-4">
            <Field label={t('projects.validFrom')}>
              <Input type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
            </Field>
            <Field label={t('projects.validTo')}>
              <Input type="date" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} />
            </Field>
          </div>
          {becomesSelected && <Notice tone="info">{t('projects.scopeSelectedHint')}</Notice>}
          {badRange && <Notice tone="warn">{t('projects.validRangeError')}</Notice>}
          <ErrorBox error={set.error} />
        </div>
      </Modal>
      <ConfirmModal
        open={!!removeId}
        onClose={() => setRemoveId(null)}
        title={t('projects.removeMember')}
        text={t('projects.removeMemberText')}
        confirmLabel={t('projects.removeMember')}
        danger
        loading={remove.isPending}
        onConfirm={() =>
          remove
            .mutateAsync({ projectId: data.project.id, personId: removeId! })
            .then(() => setRemoveId(null))
            .catch(toast.error)
        }
      />
      {tasksFor && memberIds.has(tasksFor) && <MemberTasksModal data={data} personId={tasksFor} onClose={() => setTasksFor(null)} />}
    </Panel>
  );
}

function MemberTasksModal({ data, personId, onClose }: { data: ProjectData; personId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const tasks = useQ('tasks.list', { projectId: data.project.id });
  const member = data.members.find((m) => m.personId === personId)!;
  const [sel, setSel] = useState<Set<string>>(new Set(member.selectedTaskIds));
  const save = useM('projects.setMemberTasks', { invalidate: ['projects.', 'tasks.'] });
  return (
    <Modal
      open
      onClose={onClose}
      title={`${t('projects.selectTasks')}: ${member.name}`}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() =>
              save
                .mutateAsync({ projectId: data.project.id, personId, taskIds: [...sel] })
                .then(onClose)
                .catch(() => {})
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      {tasks.isLoading && <Loading />}
      <ul className="flex max-h-96 flex-col gap-1 overflow-y-auto">
        {(tasks.data?.tasks ?? []).map((x) => (
          <li key={x.id}>
            <Checkbox
              label={
                <span>
                  <span className="num font-semibold text-accent">{x.ref}</span> {x.title}
                </span>
              }
              checked={sel.has(x.id)}
              onChange={(e) => {
                const next = new Set(sel);
                if (e.target.checked) next.add(x.id);
                else next.delete(x.id);
                setSel(next);
              }}
            />
          </li>
        ))}
      </ul>
      {sel.size === 0 && <Notice tone="warn" className="mt-3">{t('projects.noTasksSelected')}</Notice>}
      <ErrorBox error={save.error} />
    </Modal>
  );
}

function TasksTab({ data }: { data: ProjectData }) {
  const { t } = useTranslation();
  const { me, can, isLead } = useMe();
  const toast = useToast();
  const cats = useCategories();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [statusGroup, setStatusGroup] = useState('');
  const [source, setSource] = useState<SourceFilter>('all');
  const [top, setTop] = useState('');
  const [sub, setSub] = useState('');
  const [category, setCategory] = useState('');
  const [warn, setWarn] = useState(false);
  const [overPlan, setOverPlan] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  // a link from the Forma tab opens the list filtered to one type (e.g. the clash issues)
  const [fields, setFields] = useState<TaskFieldFilter>(() => (params.get('type') ? { kind: `t:${params.get('type')}`, attributes: {} } : EMPTY_FIELD_FILTER));
  const [manual, setManual] = useState(false);
  const [fromTemplate, setFromTemplate] = useState(false);
  const [edit, setEdit] = useState<EditableManualTask | null>(null);
  const mirrored = data.attributeDefs.filter((a) => a.mirrored && !a.formaDeleted);
  const locations = useQ('tasks.locations', { projectId: data.project.id }, { enabled: !!data.project.forma.projectId });
  const hasLocations = !!locations.data?.nodes.length;
  const tasks = useQ('tasks.list', {
    projectId: data.project.id,
    q: q || undefined,
    statusGroup: (statusGroup || undefined) as 'active' | undefined,
    source: source === 'all' ? undefined : source,
    locationId: sub || top || undefined,
    category: source === 'manual' && category ? category : undefined,
    formaState: warn ? 'warning' : undefined,
    overPlan: overPlan || undefined,
    includeArchived: showHidden || undefined,
    ...(source === 'manual' ? {} : fieldFilterInput(fields)),
  });
  const archive = useM('tasks.archive', { invalidate: ['tasks.', 'analysis.'] });
  const archiveMany = useM('tasks.archiveMany', { invalidate: ['tasks.', 'analysis.'] });
  const billingMany = useM('tasks.setBillingMany', { invalidate: ['tasks.', 'analysis.'] });
  const canArchive = data.permissions.manageMembers;
  type TaskRow = NonNullable<typeof tasks.data>['tasks'][number];
  const sorting = useTableSort<TaskRow>('project.tasks', {
    task: (x) => `${x.source === 'forma' ? 'a' : 'b'}${String(Number(x.ref.replace(/\D/g, '')) || 0).padStart(8, '0')}`,
    title: (x) => x.title,
    period: (x) => x.dueDate ?? x.startDate,
    status: (x) => x.status,
    plan: (x) => x.plannedMinutes,
    booked: (x) => x.bookedMinutes,
  });
  const list = useMemo(() => sorting.apply(tasks.data?.tasks ?? []), [tasks.data, sorting.sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const chunk = useChunked(list, 100, JSON.stringify([q, statusGroup, source, top, sub, category, warn, overPlan, showHidden, fields, sorting.sort]));
  const order = useMemo(() => list.map((x) => x.id), [list]);
  const sel = useRangeSelection(order);
  const chosen = list.filter((x) => sel.selected.has(x.id));
  const excludedTypes = new Set(data.issueTypes.flatMap((c) => c.types.filter((st) => st.excluded).map((st) => st.id)));
  const chosenExcluded = chosen.filter((x) => x.issueSubtypeId && excludedTypes.has(x.issueSubtypeId)).length;
  const chosenClash = chosen.filter((x) => isClashName(x.issueSubtype) || isClashName(x.issueType)).length;
  const bulkArchive = (archived: boolean) => {
    const ids = chosen.filter((x) => !!x.archived !== archived).map((x) => x.id);
    if (!ids.length) return;
    archiveMany
      .mutateAsync({ ids, archived })
      .then((r) => {
        sel.clear();
        toast.undoable(t(archived ? 'tasks.hiddenMany' : 'tasks.shownMany', { count: r.changed.length }), t('common.undo'), () =>
          archiveMany.mutateAsync({ ids: r.changed, archived: !archived }).then(() => toast.ok(t('common.undone'))).catch(toast.error),
        );
      })
      .catch(toast.error);
  };
  // billing type of several manual tasks at once (project lead, billing, office); Forma tasks keep the project rule
  const chosenManual = chosen.filter((x) => x.source === 'manual');
  const bulkBilling = (type: BillingType) =>
    billingMany
      .mutateAsync({ ids: chosenManual.map((x) => x.id), defaultBillingType: type })
      .then((r) => {
        toast.ok(t('tasks.billingSetMany', { count: r.changed }));
        if (r.skipped.length) toast.warn(t('tasks.billingSkipped', { count: r.skipped.length }));
      })
      .catch(toast.error);
  const resetFilters = () => {
    setQ('');
    setStatusGroup('');
    setSource('all');
    setTop('');
    setSub('');
    setCategory('');
    setWarn(false);
    setOverPlan(false);
    setFields(EMPTY_FIELD_FILTER);
    if (params.get('type')) {
      const next = new URLSearchParams(params);
      next.delete('type');
      setParams(next, { replace: true });
    }
  };
  const filtered = !!q || !!statusGroup || source !== 'all' || !!top || !!category || warn || overPlan || fieldFilterActive(fields);
  const seeHours = !!tasks.data?.seeHours;
  const canPlan = !!tasks.data?.canPlan;
  const mayEditManual = (createdBy: string | null) => can('projects.manage') || isLead(data.project.id) || createdBy === me.person.id;
  return (
    <div className={clsx('flex flex-col gap-3', sel.count > 0 && 'pb-20')}>
      <div className="flex flex-col gap-2 rounded-lg border border-line bg-sheet p-3">
        <div className="flex flex-wrap items-center gap-2">
          <SourceSwitch value={source} onChange={setSource} />
          <Input className="w-64" placeholder={t('tasks.search')} value={q} onChange={(e) => setQ(e.target.value)} />
          <Select className="w-40" value={statusGroup} onChange={(e) => setStatusGroup(e.target.value)} aria-label={t('common.status')}>
            <option value="">{t('tasks.allStatuses')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="done">{t('task.done')}</option>
            <option value="closed">{t('task.closed')}</option>
          </Select>
          <div className="ml-auto flex gap-2">
            {canPlan && (
              <Link
                to={`/planning?project=${data.project.id}&tab=tasks`}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line px-2.5 text-sm hover:bg-sheet-2"
              >
                <CalendarRange className="size-3.5" />
                {t('planning.openPlanning')}
              </Link>
            )}
            {data.permissions.createTasks && me.person.kind === 'employee' && (
              <Button size="sm" icon={<FolderInput className="size-3.5" />} onClick={() => setFromTemplate(true)}>
                {t('templates.fromTemplate')}
              </Button>
            )}
            {data.permissions.createTasks && (
              <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setManual(true)}>
                {t('tasks.createManual')}
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {hasLocations && source !== 'manual' && (
            <LocationSelects
              nodes={locations.data!.nodes}
              top={top}
              sub={sub}
              onChange={(a, b) => {
                setTop(a);
                setSub(b);
              }}
            />
          )}
          {source === 'manual' && (
            <Select className="w-64" value={category} onChange={(e) => setCategory(e.target.value)} aria-label={t('tasks.category')}>
              <option value="">{t('picker.allCategories')}</option>
              {cats.list.map((c) => (
                <option key={c.key} value={c.key}>
                  {cats.label(c.key)}
                </option>
              ))}
            </Select>
          )}
          {source !== 'manual' && <TaskFieldFilters data={data} value={fields} onChange={setFields} />}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Checkbox label={t('task.formaDeleted')} checked={warn} onChange={(e) => setWarn(e.target.checked)} />
          {seeHours && <Checkbox label={t('projectKpi.onlyOverPlan')} checked={overPlan} onChange={(e) => setOverPlan(e.target.checked)} />}
          {data.permissions.manageMembers && <Checkbox label={t('tasks.showHidden')} checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />}
          {filtered && (
            <button type="button" onClick={resetFilters} className="text-sm text-accent hover:underline">
              {t('tasks.resetFilters')}
            </button>
          )}
          <span className="ml-auto flex items-center gap-3 text-sm text-graphite">
            {t('tasks.count', { count: list.length })}
            {canArchive && list.length > 0 && !sel.all && (
              <button type="button" className="font-medium text-accent hover:underline" onClick={() => sel.toggleAll()}>
                {t('tasks.selectAllShown', { count: list.length })}
              </button>
            )}
          </span>
        </div>
      </div>
      {tasks.isLoading && <Loading />}
      <ErrorBox error={tasks.error} />
      {tasks.data && list.length === 0 && <EmptyState>{filtered ? t('tasks.noneFiltered') : t('tasks.none')}</EmptyState>}
      {tasks.data?.truncated && <Notice tone="warn">{t('tasks.truncated', { count: list.length })}</Notice>}
      {list.length > 0 && (
        <ScrollTable label={t('projects.tabs.tasks')}>
          <table className="w-full min-w-[1000px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                {canArchive && (
                  <th className={clsx(th, 'w-8')}>
                    <Checkbox label={<span className="sr-only">{t('common.selectAll')}</span>} checked={sel.all} onChange={sel.toggleAll} />
                  </th>
                )}
                <SortTh label={t('common.task')} sortKey="task" sort={sorting.sort} onSort={sorting.toggle} className="min-w-72" />
                <th className={th}>{t('tasks.kindColumn')}</th>
                {mirrored.map((a) => (
                  <th key={a.id} className={th}>
                    {a.title}
                  </th>
                ))}
                <SortTh label={t('tasks.period')} sortKey="period" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('common.status')} sortKey="status" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('projectKpi.plan')} sortKey="plan" sort={sorting.sort} onSort={sorting.toggle} right />
                {seeHours && <SortTh label={t('common.booked')} sortKey="booked" sort={sorting.sort} onSort={sorting.toggle} right />}
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {chunk.visible.map((x) => {
                const link = formaLink(me.settings.deepLinkTemplate, data.project.forma.projectId, x.formaIssueId);
                const use = x.plannedMinutes && x.bookedMinutes !== null ? x.bookedMinutes / x.plannedMinutes : null;
                const late = x.statusGroup === 'active' && x.dueDate && x.dueDate < me.today;
                return (
                  <tr key={x.id} className={clsx('border-b border-line/70', x.archived && 'text-graphite', sel.selected.has(x.id) && 'bg-accent-soft/50')}>
                    {canArchive && (
                      <td className={td}>
                        <Checkbox label={<span className="sr-only">{t('common.select')}</span>} {...rowCheck(sel, x.id)} />
                      </td>
                    )}
                    <td className={td}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="num font-semibold text-accent">{x.ref}</span>
                        <span>{x.title}</span>
                        <TaskBadges formaState={x.formaState} source={x.source} />
                        {x.archived && (
                          <Pill title={x.archiveReason === 'import_filter' ? t('tasks.hiddenByFilter') : x.archiveReason === 'draft' ? t('tasks.formaDraftHint') : undefined}>
                            {x.archiveReason === 'import_filter' ? t('tasks.filteredOut') : x.archiveReason === 'draft' ? t('tasks.formaDraft') : t('common.archived')}
                          </Pill>
                        )}
                      </div>
                      {x.locationPath && <div className="text-xs text-graphite">{x.locationPath}</div>}
                    </td>
                    <td className={clsx(td, 'text-graphite')}>
                      {x.source === 'forma' ? (
                        <>
                          <div>{x.issueType ?? '-'}</div>
                          {x.issueSubtype && <div className="text-xs">{x.issueSubtype}</div>}
                        </>
                      ) : (
                        cats.label(x.manualCategory)
                      )}
                    </td>
                    {mirrored.map((a) => (
                      <td key={a.id} className={clsx(td, 'max-w-48 text-graphite')}>
                        <span className="line-clamp-2">{x.attributes[a.id] ?? ''}</span>
                      </td>
                    ))}
                    <td className={clsx(td, 'num whitespace-nowrap', late ? 'font-semibold text-bad' : 'text-graphite')}>
                      {x.startDate || x.dueDate ? `${x.startDate ? d(x.startDate) : '…'} - ${x.dueDate ? d(x.dueDate) : '…'}` : '-'}
                    </td>
                    <td className={clsx(td, 'whitespace-nowrap')}>{t(`task.status.${x.status as 'open'}`, { defaultValue: x.status })}</td>
                    <td className={clsx(td, 'num text-right whitespace-nowrap')}>
                      {x.plannedMinutes !== null ? (x.plannedMinutes === 0 ? t('projectKpi.notEvaluated') : h(x.plannedMinutes, 1)) : <span className="text-graphite">-</span>}
                    </td>
                    {seeHours && (
                      <td className={clsx(td, 'num text-right whitespace-nowrap')}>
                        <span className="inline-flex items-center gap-2">
                          {!!x.plannedMinutes && <PlanBar planned={x.plannedMinutes} booked={x.bookedMinutes ?? 0} className="w-16" />}
                          <span className={clsx(use !== null && use > 1 && 'font-semibold text-warn')}>{h(x.bookedMinutes)}</span>
                          {use !== null && <span className="w-10 text-xs text-graphite">{pct(use)}</span>}
                        </span>
                      </td>
                    )}
                    <td className={clsx(td, 'text-right whitespace-nowrap')}>
                      {link && (
                        <FormaIssueLink href={link} className="mr-1 inline-flex rounded p-1.5 text-graphite hover:text-accent" title={t('task.openInForma')}>
                          <ExternalLink className="size-3.5" />
                        </FormaIssueLink>
                      )}
                      {x.source === 'manual' && mayEditManual(x.createdBy) && (
                        <IconButton
                          label={t('common.edit')}
                          icon={<Pencil className="size-3.5" />}
                          onClick={() =>
                            setEdit({
                              id: x.id,
                              ref: x.ref,
                              title: x.title,
                              description: x.description,
                              manualCategory: x.manualCategory,
                              defaultBillingType: x.defaultBillingType,
                              billingTypeFixed: x.billingTypeFixed,
                              plannedMinutes: x.plannedMinutes,
                              startDate: x.startDate,
                              dueDate: x.dueDate,
                              statusGroup: x.statusGroup,
                              bookedMinutes: x.bookedMinutes,
                              mayDelete: data.permissions.manageMembers,
                            })
                          }
                        />
                      )}
                      {data.permissions.manageMembers && (
                        <IconButton
                          label={x.archived ? t('common.unarchive') : t('tasks.hide')}
                          icon={x.archived ? <ArchiveRestore className="size-3.5" /> : <Archive className="size-3.5" />}
                          onClick={() => archive.mutateAsync({ id: x.id, archived: !x.archived }).catch(toast.error)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {chunk.hasMore && <Sentinel onMore={chunk.more} />}
        </ScrollTable>
      )}
      {chunk.hasMore && <p className="text-xs text-graphite">{t('entries.showing', { shown: chunk.shown, total: chunk.total })}</p>}
      <p className="text-xs text-graphite">{t('tasks.listLegend')}</p>
      {canArchive && sel.count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-sheet px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] lg:left-60">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2">
            <span className="text-sm">{t('common.selected', { count: sel.count })}</span>
            <span className="text-xs text-graphite">{t('common.shiftHint')}</span>
            {(chosenClash > 0 || chosenExcluded > 0) && data.permissions.sync && (
              <span className="text-xs text-graphite">
                {t('tasks.clashHint')}{' '}
                <Link to={`/projects/${data.project.id}?tab=forma`} className="font-medium text-accent hover:underline">
                  {t('tasks.toImportFilter')}
                </Link>
              </span>
            )}
            <div className="ml-auto flex flex-wrap gap-2">
              {data.permissions.taskBilling && chosenManual.length > 0 && (
                <Select
                  className="w-56"
                  value=""
                  aria-label={t('tasks.billingMany')}
                  disabled={billingMany.isPending}
                  onChange={(e) => e.target.value && bulkBilling(e.target.value as BillingType)}
                >
                  <option value="">{t('tasks.billingMany')}</option>
                  {BILLING_TYPES.map((b) => (
                    <option key={b} value={b}>
                      {t(`billingType.${b}`)}
                    </option>
                  ))}
                </Select>
              )}
              <Button onClick={sel.clear}>{t('common.cancel')}</Button>
              {chosen.some((x) => x.archived) && (
                <Button icon={<ArchiveRestore className="size-4" />} loading={archiveMany.isPending} onClick={() => bulkArchive(false)}>
                  {t('tasks.showSelected')}
                </Button>
              )}
              {chosen.some((x) => !x.archived) && (
                <Button variant="primary" icon={<Archive className="size-4" />} loading={archiveMany.isPending} onClick={() => bulkArchive(true)}>
                  {t('tasks.hideSelected')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <ManualTaskDialog open={manual} onClose={() => setManual(false)} projectId={data.project.id} />
      <ManualTaskDialog open={!!edit} onClose={() => setEdit(null)} projectId={data.project.id} task={edit} />
      <ApplyTemplateDialog open={fromTemplate} onClose={() => setFromTemplate(false)} projectId={data.project.id} />
    </div>
  );
}


function FormaTab({ data }: { data: ProjectData }) {
  const { t } = useTranslation();
  const toast = useToast();
  const f = data.project.forma;
  const [linking, setLinking] = useState(false);
  const formaProjects = useQ('forma.projects', {}, { enabled: linking });
  const link = useM('projects.linkForma', { invalidate: ['projects.', 'forma.'] });
  const config = useM('projects.setSyncConfig', { invalidate: ['projects.', 'tasks.'] });
  const sync = useM('forma.syncNow', { invalidate: ['projects.', 'tasks.', 'forma.', 'dashboard.', 'analysis.'] });
  const canEdit = data.permissions.edit;
  const run = (full: boolean) =>
    sync
      .mutateAsync({ projectId: data.project.id, full })
      .then((r) => {
        const o = r.results[0]?.outcome;
        if (o && 'stats' in o) toast.ok(t('projects.syncResult', { created: o.stats.created, updated: o.stats.updated, flagged: o.stats.flaggedDeleted + o.stats.flaggedAccessLost, excluded: o.stats.excluded ?? 0 }));
        if (o && o.status === 'error') toast.error(o.error);
      })
      .catch(toast.error);
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title={f.projectId ? t('projects.formaLinked') : t('projects.formaNotLinked')}
          actions={
            canEdit &&
            (f.projectId ? (
              <Button size="sm" variant="danger" icon={<Unlink className="size-3.5" />} onClick={() => link.mutateAsync({ projectId: data.project.id, hubId: null, formaProjectId: null }).catch(toast.error)}>
                {t('projects.formaUnlink')}
              </Button>
            ) : (
              <Button size="sm" icon={<Link2 className="size-3.5" />} onClick={() => setLinking(true)}>
                {t('projects.formaLink')}
              </Button>
            ))
          }
        >
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-graphite">{t('projects.formaProject')}</dt>
            <dd>{f.name ?? '-'}</dd>
            <dt className="text-graphite">{t('common.status')}</dt>
            <dd>
              <Pill tone={FORMA_TONE[f.state]}>{t(`projects.formaState.${f.state as 'active'}`)}</Pill>
            </dd>
            <dt className="text-graphite">{t('projects.lastSync')}</dt>
            <dd className="num">{dt(f.lastSyncAt)}</dd>
            {f.lastRun && (
              <>
                <dt className="text-graphite">{t('admin.forma.runs')}</dt>
                <dd className="num">
                  {t(`admin.forma.runStatus.${f.lastRun.status as 'ok'}`)} ·{' '}
                  {t('projects.runStats', { fetched: f.lastRun.stats.fetched ?? 0, created: f.lastRun.stats.created ?? 0, updated: f.lastRun.stats.updated ?? 0, excluded: f.lastRun.stats.excluded ?? 0 })}
                  {!!f.lastRun.stats.drafts && <div className="text-xs text-graphite">{t('projects.runDrafts', { count: f.lastRun.stats.drafts })}</div>}
                  {f.lastRun.error && <div className="text-xs text-bad">{f.lastRun.error}</div>}
                </dd>
              </>
            )}
          </dl>
          <p className="mt-3 text-xs text-graphite">{t('projects.formaStateHint')}</p>
          {f.projectId && data.permissions.sync && (
            <div className="mt-3 flex gap-2">
              <Button icon={<RefreshCw className="size-4" />} loading={sync.isPending} onClick={() => run(false)}>
                {t('projects.syncNow')}
              </Button>
              <Button variant="ghost" loading={sync.isPending} onClick={() => run(true)}>
                {t('projects.fullSync')}
              </Button>
            </div>
          )}
        </Panel>
        {f.projectId && (
          <Panel title={t('projects.whatIsRead')}>
            <ul className="mb-3 flex list-disc flex-col gap-1 pl-5 text-sm text-graphite">
              <li>{t('projects.readFields')}</li>
              <li>{t('projects.readNotHours')}</li>
              <li>{t('projects.readOneWay')}</li>
            </ul>
            {canEdit && (
              <div className="flex flex-col gap-2 border-t border-line pt-3">
                <Checkbox label={t('projects.syncEnabled')} checked={f.syncEnabled} onChange={(e) => config.mutateAsync({ projectId: data.project.id, syncEnabled: e.target.checked }).catch(toast.error)} />
                <Checkbox
                  label={t('projects.includeClosed')}
                  hint={t('projects.includeClosedHint')}
                  checked={f.syncConfig.includeClosed !== false}
                  onChange={(e) => config.mutateAsync({ projectId: data.project.id, includeClosed: e.target.checked }).catch(toast.error)}
                />
                <Checkbox
                  label={t('projects.includeDrafts')}
                  hint={t('projects.includeDraftsHint')}
                  checked={f.syncConfig.includeDrafts === true}
                  onChange={(e) => config.mutateAsync({ projectId: data.project.id, includeDrafts: e.target.checked }).catch(toast.error)}
                />
                <Checkbox
                  label={t('projects.formaNameFollow')}
                  hint={t('projects.formaNameFollowHint')}
                  checked={data.project.formaNameFollow !== false}
                  onChange={(e) => config.mutateAsync({ projectId: data.project.id, formaNameFollow: e.target.checked }).catch(toast.error)}
                />
              </div>
            )}
          </Panel>
        )}
      </div>
      {f.projectId && data.permissions.sync && <ImportFilterPanel data={data} onSync={() => run(true)} syncing={sync.isPending} />}
      {f.projectId && <FieldsPanel data={data} onSync={() => run(true)} syncing={sync.isPending} />}
      <Modal open={linking} onClose={() => setLinking(false)} title={t('projects.formaLink')}>
        {formaProjects.isLoading && <Loading />}
        <ErrorBox error={formaProjects.error} />
        <ul className="flex flex-col">
          {(formaProjects.data?.projects ?? []).map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2 border-b border-line/60 py-2 text-sm">
              <span>
                {p.name}
                {p.linkedTo && <span className="ml-2 text-xs text-graphite">({p.linkedTo.number})</span>}
              </span>
              <Button
                size="sm"
                disabled={!!p.linkedTo}
                onClick={() =>
                  link
                    .mutateAsync({ projectId: data.project.id, hubId: formaProjects.data!.hubId, formaProjectId: p.id, formaName: p.name })
                    .then(() => {
                      setLinking(false);
                      return run(true);
                    })
                    .catch(toast.error)
                }
              >
                {t('common.select')}
              </Button>
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
}

/**
 * Import filter: the categories and types of the Forma project. Clash issues of the model coordination (category
 * "Coordination", locked type "Clash") are left out by default. Leaving a type out hides its tasks at once (those
 * with hours stay); "Bereinigen" deletes the hidden ones without hours from Projektzeit (they stay in Forma).
 */
function ImportFilterPanel({ data, onSync, syncing }: { data: ProjectData; onSync: () => void; syncing: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const view = useQ('forma.importFilter', { projectId: data.project.id });
  const save = useM('forma.setImportFilter', { invalidate: ['forma.', 'projects.', 'tasks.', 'analysis.'] });
  const cleanup = useM('forma.cleanupExcluded', { invalidate: ['forma.', 'projects.', 'tasks.', 'analysis.'] });
  const [excluded, setExcluded] = useState<Set<string> | null>(null);
  const [confirmClean, setConfirmClean] = useState(false);
  const v = view.data;
  const stored = useMemo(() => new Set((v?.categories ?? []).flatMap((c) => c.types.filter((x) => x.excluded).map((x) => x.id))), [v]);
  useEffect(() => setExcluded(null), [v]);
  const current = excluded ?? stored;
  const dirty = excluded !== null && (excluded.size !== stored.size || [...excluded].some((id) => !stored.has(id)));
  const hiddenTotal = (v?.categories ?? []).reduce((s, c) => s + c.types.reduce((x, y) => x + (y.excluded ? y.hidden : 0), 0), 0);
  const keptTotal = (v?.categories ?? []).reduce((s, c) => s + c.types.reduce((x, y) => x + (y.excluded ? y.visible : 0), 0), 0);
  const toggle = (ids: string[], include: boolean) => {
    const next = new Set(current);
    for (const id of ids) {
      if (include) next.delete(id);
      else next.add(id);
    }
    setExcluded(next);
  };
  const onSave = () =>
    save
      .mutateAsync({ projectId: data.project.id, excludedSubtypeIds: [...current] })
      .then((r) => {
        toast.ok(t('projects.filterSaved', { hidden: r.hidden, shown: r.shown, kept: r.keptWithHours }));
        onSync();
      })
      .catch(toast.error);
  return (
    <Panel
      title={t('projects.importFilter')}
      actions={
        <>
          {hiddenTotal > 0 && (
            <Button size="sm" variant="danger" icon={<Trash2 className="size-3.5" />} onClick={() => setConfirmClean(true)}>
              {t('projects.cleanupExcluded', { count: hiddenTotal })}
            </Button>
          )}
          <Button size="sm" variant="primary" disabled={!dirty} loading={save.isPending || syncing} onClick={onSave}>
            {t('projects.saveFilter')}
          </Button>
        </>
      }
    >
      <Notice tone="info" className="mb-3">
        {t('projects.clashInfo')}
      </Notice>
      {view.isLoading && <Loading />}
      <ErrorBox error={view.error} />
      {v && v.categories.length === 0 && <p className="text-sm text-graphite">{t('projects.noTypesYet')}</p>}
      {v && v.categories.length > 0 && (
        <div className="scroll-x">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={clsx(th, 'w-28')}>{t('projects.take')}</th>
                <th className={th}>{t('projects.categoryType')}</th>
                <th className={clsx(th, 'text-right')}>{t('projects.inProjektzeit')}</th>
                <th className={clsx(th, 'text-right')}>{t('projects.hiddenByFilter')}</th>
                <th className={clsx(th, 'text-right')}>{t('projects.withHours')}</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {v.categories.map((c) => {
                const ids = c.types.map((x) => x.id);
                const allIn = ids.every((id) => !current.has(id));
                const someIn = ids.some((id) => !current.has(id));
                return (
                  <Fragment key={c.id}>
                    <tr className="border-b border-line bg-sheet-2/60">
                      <td className={td}>
                        <Checkbox
                          label={<span className="sr-only">{c.title}</span>}
                          checked={allIn}
                          indeterminate={someIn && !allIn}
                          onChange={(e) => toggle(ids, e.target.checked)}
                        />
                      </td>
                      <td className={clsx(td, 'font-semibold')} colSpan={5}>
                        {c.title}
                        {c.clash && <Pill tone="warn" className="ml-2">{t('projects.clashBadge')}</Pill>}
                        {!c.active && <span className="ml-2 text-xs font-normal text-graphite">({t('projects.inactiveInForma')})</span>}
                      </td>
                    </tr>
                    {c.types.map((x) => {
                      const include = !current.has(x.id);
                      return (
                        <tr key={x.id} className={clsx('border-b border-line/60', !include && 'text-graphite')}>
                          <td className={clsx(td, 'pl-7')}>
                            <Checkbox label={<span className="sr-only">{x.title}</span>} checked={include} onChange={(e) => toggle([x.id], e.target.checked)} />
                          </td>
                          <td className={clsx(td, 'pl-6')}>
                            {x.title}
                            {x.clash && <Pill tone="warn" className="ml-2">{t('projects.clashBadge')}</Pill>}
                            {!include && <Pill className="ml-2">{t('projects.notTaken')}</Pill>}
                            {!x.active && <span className="ml-2 text-xs text-graphite">({t('projects.inactiveInForma')})</span>}
                          </td>
                          <td className={clsx(td, 'num text-right')}>{x.visible}</td>
                          <td className={clsx(td, 'num text-right')}>{x.hidden || '-'}</td>
                          <td className={clsx(td, 'num text-right')}>{x.booked || '-'}</td>
                          <td className={clsx(td, 'text-right')}>
                            {x.visible + x.hidden > 0 && (
                              <Link to={`/projects/${data.project.id}?tab=tasks&type=${encodeURIComponent(x.id)}`} className="text-xs font-medium text-accent hover:underline">
                                {t('projects.showTasks')}
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {v && (
        <div className="mt-3 flex flex-col gap-1 text-xs text-graphite">
          {v.lastFullExcluded !== null && <span>{t('projects.lastFullExcluded', { count: v.lastFullExcluded, date: dt(v.lastFullSyncAt) })}</span>}
          {keptTotal > 0 && <span>{t('projects.keptWithHours', { count: keptTotal })}</span>}
          <span>{t('projects.filterHint')}</span>
        </div>
      )}
      <ConfirmModal
        open={confirmClean}
        onClose={() => setConfirmClean(false)}
        title={t('projects.cleanupTitle')}
        text={t('projects.cleanupText', { count: hiddenTotal })}
        confirmLabel={t('projects.cleanupConfirm')}
        danger
        loading={cleanup.isPending}
        error={cleanup.error}
        onConfirm={() =>
          cleanup
            .mutateAsync({ projectId: data.project.id })
            .then((r) => {
              toast.ok(t('projects.cleanupDone', { deleted: r.deleted, kept: r.keptWithHours }));
              setConfirmClean(false);
            })
            .catch(() => {})
        }
      />
    </Panel>
  );
}

/**
 * Custom fields of the Forma project: which are taken over into Projektzeit (read only). Taken over fields appear in
 * the task lists (column and filter) and in the project analysis (floor, building, trade, work stage, more filters).
 */
function FieldsPanel({ data, onSync, syncing }: { data: ProjectData; onSync: () => void; syncing: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  // values the project uses per field (request 20): read with the right to change them
  const scopeQ = useQ('analysis.scope', { projectId: data.project.id }, { enabled: data.permissions.scope && data.attributeDefs.some((a) => a.mirrored) });
  const scopeSummary = useScopeSummary();
  const [scopeField, setScopeField] = useState<string | null>(null);
  const f = data.project.forma;
  const config = useM('projects.setSyncConfig', { invalidate: ['projects.', 'tasks.', 'analysis.'] });
  const refresh = useM('forma.refreshAttributes', { invalidate: ['projects.'] });
  const canEdit = data.permissions.sync;
  const stored = useMemo(() => new Set(f.syncConfig.mirroredAttributeIds ?? data.attributeDefs.filter((a) => a.mirrored).map((a) => a.id)), [f.syncConfig.mirroredAttributeIds, data.attributeDefs]);
  const [mirrored, setMirrored] = useState<Set<string>>(stored);
  useEffect(() => setMirrored(stored), [stored]);
  const [q, setQ] = useState('');
  const [onlyTaken, setOnlyTaken] = useState(false);
  const dirty = mirrored.size !== stored.size || [...mirrored].some((id) => !stored.has(id));
  const rows = data.attributeDefs.filter((a) => (!onlyTaken || mirrored.has(a.id)) && matchesSearch(`${a.title} ${a.dataType}`, q));
  const roleLabel = (r: string | null) => (r ? t(`projects.fieldRoles.${r as 'floor'}`) : '');
  return (
    <Panel
      title={t('projects.fieldsTitle', { taken: mirrored.size, total: data.attributeDefs.length })}
      actions={
        canEdit && (
          <>
            <Button size="sm" variant="ghost" icon={<RefreshCw className="size-3.5" />} loading={refresh.isPending} onClick={() => refresh.mutateAsync({ projectId: data.project.id }).then((r) => toast.ok(t('projects.fieldsReloaded', { count: r.count }))).catch(toast.error)}>
              {t('projects.reloadFields')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!dirty}
              loading={config.isPending || syncing}
              onClick={() =>
                config
                  .mutateAsync({ projectId: data.project.id, mirroredAttributeIds: [...mirrored] })
                  .then(() => {
                    toast.ok(t('projects.fieldsSaved'));
                    onSync();
                  })
                  .catch(toast.error)
              }
            >
              {t('projects.saveFields')}
            </Button>
          </>
        )
      }
    >
      <p className="mb-3 text-sm text-graphite">{t('projects.fieldsIntro')}</p>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative w-72">
          <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-faint" />
          <Input className="pl-8" placeholder={t('projects.searchFields')} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Checkbox label={t('projects.onlyTaken')} checked={onlyTaken} onChange={(e) => setOnlyTaken(e.target.checked)} />
      </div>
      {data.attributeDefs.length === 0 ? (
        <p className="text-sm text-graphite">{t('projects.noFieldsYet')}</p>
      ) : (
        <div className="scroll-x max-h-[480px] overflow-y-auto rounded-md border border-line">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="sticky top-0 border-b border-line bg-sheet-2">
              <tr>
                <th className={clsx(th, 'w-28')}>{t('projects.take')}</th>
                <th className={th}>{t('projects.field')}</th>
                <th className={th}>{t('projects.fieldType')}</th>
                <th className={clsx(th, 'text-right')}>{t('projects.fieldValues')}</th>
                <th className={th}>{t('projects.fieldRole')}</th>
                {data.permissions.scope && <th className={th}>{t('scope.col')}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className={clsx('border-b border-line/60', !mirrored.has(a.id) && 'text-graphite')}>
                  <td className={td}>
                    <Checkbox
                      label={<span className="sr-only">{a.title}</span>}
                      checked={mirrored.has(a.id)}
                      disabled={!canEdit}
                      onChange={(e) => {
                        const next = new Set(mirrored);
                        if (e.target.checked) next.add(a.id);
                        else next.delete(a.id);
                        setMirrored(next);
                      }}
                    />
                  </td>
                  <td className={td}>
                    {a.title}
                    {a.formaDeleted && <Pill tone="bad" className="ml-2">{t('projects.fieldDeleted')}</Pill>}
                  </td>
                  <td className={clsx(td, 'text-graphite')}>{t(`projects.fieldTypes.${a.dataType as 'list'}`, { defaultValue: a.dataType })}</td>
                  <td className={clsx(td, 'num text-right text-graphite')}>{a.dataType === 'list' ? a.optionCount : mirrored.has(a.id) ? (data.attributeValues[a.id]?.length ?? 0) : '-'}</td>
                  <td className={td}>{a.analysisRole && <Pill tone="accent">{roleLabel(a.analysisRole)}</Pill>}</td>
                  {data.permissions.scope && (
                    <td className={clsx(td, 'whitespace-nowrap')}>
                      {a.mirrored && stored.has(a.id) && scopeQ.data?.fields.some((f) => f.id === a.id) && (
                        <span className="flex items-center gap-1">
                          <span className="text-xs text-graphite">{scopeSummary(scopeQ.data.scope[a.id], scopeQ.data.fields.find((f) => f.id === a.id)!.values.length)}</span>
                          <FieldScopeButton label={t('scope.button')} onClick={() => setScopeField(a.id)} />
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(data.analysisLocations.floor || data.analysisLocations.building) && (
        <p className="mt-2 text-xs text-graphite">{t('projects.locationsUsed', { floor: data.analysisLocations.floor ?? '-', building: data.analysisLocations.building ?? '-' })}</p>
      )}
      <p className="mt-2 text-xs text-graphite">{t('projects.fieldsHint')}</p>
      <FieldScopeDialog projectId={data.project.id} fieldId={scopeField} open={!!scopeField} onClose={() => setScopeField(null)} />
    </Panel>
  );
}

function FeeTab({ data }: { data: ProjectData }) {
  const { t } = useTranslation();
  const toast = useToast();
  const initial = data.feePhases.length
    ? data.feePhases
    : HOAI_TGA_PHASES.map((p) => ({ key: p.key, label: p.label, weightPercent: p.weight, feeCents: Math.round(((data.project.feeTotalCents ?? 0) * p.weight) / 100) }));
  const [phases, setPhases] = useState(initial);
  const save = useM('projects.setFeePhases', { invalidate: ['projects.', 'billing.'] });
  const total = phases.reduce((s, p) => s + p.feeCents, 0);
  const resetHoai = () => {
    const base = total || data.project.feeTotalCents || 0;
    setPhases(HOAI_TGA_PHASES.map((p) => ({ key: p.key, label: p.label, weightPercent: p.weight, feeCents: Math.round((base * p.weight) / 100) })));
  };
  return (
    <Panel
      title={t('projects.feePhases')}
      actions={
        <>
          <Button size="sm" variant="ghost" onClick={resetHoai}>
            {t('projects.resetHoai')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={save.isPending}
            onClick={() =>
              save
                .mutateAsync({ projectId: data.project.id, phases })
                .then(() => toast.ok(t('common.saved')))
                .catch(toast.error)
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-graphite">{t('projects.feeHint')}</p>
      <div className="scroll-x">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="border-b border-line">
            <tr>
              <th className={th}>{t('billing.phase')}</th>
              <th className={clsx(th, 'text-right')}>{t('billing.weight')}</th>
              <th className={clsx(th, 'text-right')}>{t('billing.fee')}</th>
            </tr>
          </thead>
          <tbody>
            {phases.map((p, i) => (
              <tr key={p.key} className="border-b border-line/70">
                <td className={td}>
                  <Input value={p.label} onChange={(e) => setPhases(phases.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                </td>
                <td className={clsx(td, 'w-28')}>
                  <Input
                    type="number"
                    className="num text-right"
                    value={p.weightPercent ?? ''}
                    onChange={(e) => setPhases(phases.map((x, j) => (j === i ? { ...x, weightPercent: e.target.value === '' ? null : Number(e.target.value) } : x)))}
                  />
                </td>
                <td className={clsx(td, 'w-44')}>
                  <MoneyInput value={p.feeCents} onChange={(c) => setPhases(phases.map((x, j) => (j === i ? { ...x, feeCents: c ?? 0 } : x)))} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-semibold">
              <td className={td}>{t('common.total')}</td>
              <td className={clsx(td, 'num text-right')}>{phases.reduce((s, p) => s + (p.weightPercent ?? 0), 0)}</td>
              <td className={clsx(td, 'num text-right')}>{money(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <ErrorBox error={save.error} />
    </Panel>
  );
}
